import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import authService from '../services/authService';
import { AuthContextType, User } from '../types';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null); // Firebase user
  const [backendUser, setBackendUser] = useState<User | null>(null); // Laravel user with DB info
  const [loading, setLoading] = useState<boolean>(true);

  // Sign in with Google. Backend sync happens in onAuthStateChanged below,
  // which is the single source of truth for /auth/login calls.
  const signInWithGoogle = async (): Promise<FirebaseUser> => {
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setLoading(false);
      throw error;
    }
  };

  // Sign out
  const logout = async (): Promise<void> => {
    try {
      setLoading(true);

      // Get current ID token before signing out
      if (user) {
        const idToken = await user.getIdToken();
        // Notify backend - if this fails, prevent logout
        try {
          await authService.logout(idToken);
        } catch (backendError) {
          console.error('Backend connection failed, cannot logout:', backendError);
          throw new Error('Cannot logout: Backend service unavailable');
        }
      }

      // Sign out from Firebase only if backend logout succeeded
      await signOut(auth);
      setBackendUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Listen to Firebase auth state changes. This is the single source of truth
  // for syncing with the Laravel backend — fires on both fresh sign-in and reload.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const idToken = await currentUser.getIdToken();
          const response = await authService.login(idToken);
          setBackendUser(response.user);
        } catch (error) {
          console.error('Failed to sync user with backend, rolling back:', error);
          setBackendUser(null);
          await signOut(auth);
        }
      } else {
        setBackendUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value: AuthContextType = {
    user, // Firebase user object
    backendUser, // Laravel user with database info (id, firebase_uid, email, name, etc.)
    loading,
    signInWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
